import { useMemo } from "react";
import { type Locale } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { fromTimestamp } from "@racoon/lib";
import { Namespace, TranslationKey, useDateFnsLocale, useT, type TFn } from "@racoon/i18n";
import { JOB_TYPE_CATEGORY_KEY } from "~/shared/lib/translationMaps";
import {
  scheduleDays,
  scheduleDivisionId,
  scheduleJobType,
  scheduleLive,
  scheduleWeekdays,
  todayKey,
} from "~/shared/lib/container-group-schedule";
import {
  isLegacySchedule,
  scheduleToForm,
} from "~/features/container-group-schedule/lib/schedule-form";
import { describeRules, periodText } from "~/features/container-group-schedule/lib/schedule-label";
import {
  dayOfMonthMaskToWeeks,
  weeksOfMonthLabel,
} from "~/features/pickup-location-modal/model/week-of-month";
import { type ReportContainerGroup, useReportContainerGroups } from "./use-report-data";
import { compareIds } from "./job-report";
import { useReportFilterValues } from "./use-report-filters";

/** One row of «Перечень графиков вывоза + МГ» (AIS 15.1.4): one row of the schedule table. */
export interface ScheduleReportRow {
  scheduleId: bigint;
  containerGroupId: bigint;
  pickupLocationId: bigint;
  pickupLocationName: string;
  ownerOrganizationId: string;
  ownerOrganizationName: string;
  contractDealNumber: string;
  contractEndAt: Date | null;
  /** Pickup location groups linking this container group for this job type, comma-separated. */
  pickupLocationGroups: string;
  /** Enum CATEGORY_* key of the job type; empty for a job type without one. */
  jobCategory: string;
  containerTypeName: string;
  numberOfContainers: number;
  /** "JOB_TYPE_*" key of the schedule. */
  jobType: string;
  jobTypeLabel: string;
  /** «Ответственное депо»: the schedule's provider, else the group's default. */
  serviceProviderName: string;
  /** "01.11.2026 — бессрочно". */
  period: string;
  /** "YYYY-MM-DD"; validTo "" = open-ended. The period column sorts by them. */
  validFrom: string;
  validTo: string;
  /** Readable rules; a legacy row's cron string, read the same way, or raw when it does not parse. */
  params: string;
  /** Days of week the schedule runs on at all, 0=Sunday .. 6=Saturday: the row's cells. */
  weekdays: number[];
  /** Those it runs on every week of every month: what the weekday totals count. */
  weekdaysEveryWeek: number[];
}

/** One row of «Реестр площадок» (AIS 15.3.1): a pickup location with the container groups it hosts. */
export interface PickupLocationRegistryRow {
  pickupLocationId: bigint;
  pickupLocationName: string;
  /** Distinct owners of the pickup location's container groups, by name. */
  ownerOrganizations: { id: string; name: string }[];
  /** Distinct Enum CATEGORY_* keys across the pickup location's schedules. */
  jobCategories: string[];
  /** e.g. "2 × Евро 1.1, 1 × Бункер 8". */
  containers: string;
  /** "<job type>: <schedule>" per distinct schedule, "; "-separated. */
  schedules: string;
}

/** Report columns Пн … Вс; dow is the expression's day of week, 0=Sunday. */
export const WEEKDAYS = [
  { id: "mon", dow: 1, key: "schedules.weekday.mon" },
  { id: "tue", dow: 2, key: "schedules.weekday.tue" },
  { id: "wed", dow: 3, key: "schedules.weekday.wed" },
  { id: "thu", dow: 4, key: "schedules.weekday.thu" },
  { id: "fri", dow: 5, key: "schedules.weekday.fri" },
  { id: "sat", dow: 6, key: "schedules.weekday.sat" },
  { id: "sun", dow: 0, key: "schedules.weekday.sun" },
] as const;

/** Days a legacy day-of-week mask covers: "*", lists and ranges (go-backend internal/common/schedule). */
const dayOfWeekMask = (mask: string): number[] => {
  if (mask === "*") return WEEKDAYS.map((day) => day.dow);
  const days = new Set<number>();
  for (const part of mask.split(",")) {
    const [from, to = from] = part.split("-").map(Number);
    for (let day = from; day <= to && day <= 6; day++) days.add(day);
  }
  return [...days];
};

/** A legacy row's rules: "RULE(; RULE)* (| from | to)?", RULE = "[windows] dom month dow". */
const parseLegacySchedule = (expression: string) =>
  expression
    .split("|")[0]
    .split(";")
    .map((rule) => rule.trim().split(/\s+/))
    .filter((fields) => fields.length === 4)
    .map(([windows, dayOfMonth, month, dayOfWeek]) => ({
      windows: [...windows.matchAll(/\[(.*?)\]/g)].map((match) => match[1].replace("-", "–")),
      weeks: dayOfMonthMaskToWeeks(dayOfMonth),
      days: dayOfWeekMask(dayOfWeek),
      // Every week of every month, as scheduleWeekdays counts typed rows.
      everyWeek: dayOfMonth === "*" && month === "*",
    }));

/** "Пн, Ср 06:00–12:00", with the weeks of the month when the rule is not weekly. */
const describeLegacySchedule = (t: TFn, rules: ReturnType<typeof parseLegacySchedule>) =>
  rules
    .map(({ windows, weeks, days }) => {
      const dayNames = WEEKDAYS.filter((day) => days.includes(day.dow))
        .map((day) => t(Namespace.Analytics, day.key))
        .join(", ");
      const text = [dayNames, windows.join(", ")].filter(Boolean).join(" ");
      return weeks ? `${text} (${weeksOfMonthLabel(t, weeks)})` : text;
    })
    .join("; ");

/** The rules as one line and the weekdays they serve on, typed or legacy. */
const scheduleParams = (schedule: ConnectProto.ContainerGroupSchedule, t: TFn, locale: Locale) => {
  if (!isLegacySchedule(schedule)) {
    return {
      params: describeRules(scheduleToForm(schedule), t, locale),
      weekdays: scheduleDays(schedule),
      weekdaysEveryWeek: scheduleWeekdays(schedule),
    };
  }
  const expression = schedule.legacyExpr ?? "";
  const rules = parseLegacySchedule(expression);
  return {
    params: rules.length ? describeLegacySchedule(t, rules) : expression,
    weekdays: [...new Set(rules.flatMap((rule) => rule.days))],
    weekdaysEveryWeek: [
      ...new Set(rules.filter((rule) => rule.everyWeek).flatMap((rule) => rule.days)),
    ],
  };
};

const distinct = (values: string[]) => [...new Set(values.filter(Boolean))];

const byPickupLocationThenJobType = (a: ScheduleReportRow, b: ScheduleReportRow) =>
  a.pickupLocationName.localeCompare(b.pickupLocationName) ||
  a.jobTypeLabel.localeCompare(b.jobTypeLabel) ||
  compareIds(a.containerGroupId, b.containerGroupId) ||
  compareIds(a.scheduleId, b.scheduleId);

/**
 * Who serves a schedule, as the generator decides: on a zoned group its own division while that
 * is still in the zone, else the zone's first; without a zone, the contract's provider.
 */
const scheduleDepot = (schedule: ConnectProto.ContainerGroupSchedule, cg: ReportContainerGroup) => {
  const division = scheduleDivisionId(
    schedule,
    cg.zone.map((org) => org.id),
  );
  if (division === undefined) return cg.contractProviderName ?? "";
  return cg.zone.find((org) => org.id === division)?.name ?? "";
};

/**
 * The organization's container groups with their schedule rows still in force or
 * ahead, narrowed by owner and container type.
 */
function useScheduledGroups(serviceReceiver: string, containerType: string) {
  const t = useT();
  const locale = useDateFnsLocale();
  const { rows, pickupLocations, isLoading, error, schedulesFailed } = useReportContainerGroups();
  // A dependency below, so schedules that ended yesterday drop once the date turns.
  const today = todayKey();

  const groups = useMemo(
    () =>
      rows
        .filter(
          (cg) =>
            (!serviceReceiver || String(cg.ownerOrganizationId) === serviceReceiver) &&
            (!containerType || String(cg.containerTypeId ?? "") === containerType),
        )
        .map((cg) => {
          const base = {
            containerGroupId: cg.id,
            pickupLocationId: cg.pickupLocationId,
            pickupLocationName: cg.pickupLocationName,
            ownerOrganizationId: String(cg.ownerOrganizationId),
            ownerOrganizationName: cg.ownerOrganizationName ?? "",
            contractDealNumber: cg.contractDealNumber ?? "",
            contractEndAt: fromTimestamp(cg.contractEndAt),
            containerTypeName: cg.containerTypeName ?? "",
            numberOfContainers: cg.numberOfContainers ?? 0,
          };
          const schedules = cg.schedules
            .filter((schedule) => scheduleLive(schedule, today))
            .map((schedule): ScheduleReportRow => {
              const jobType = scheduleJobType(schedule);
              return {
                ...base,
                scheduleId: schedule.id,
                pickupLocationGroups: cg.pickupLocationGroups
                  .filter((link) => link.jobType === jobType)
                  .map((link) => link.name)
                  .join(", "),
                jobCategory: JOB_TYPE_CATEGORY_KEY[schedule.jobType] ?? "",
                jobType,
                jobTypeLabel: jobType ? t(Namespace.Enum, jobType as TranslationKey) : "",
                serviceProviderName: scheduleDepot(schedule, cg),
                period: periodText(schedule.validFrom, schedule.validTo, t),
                validFrom: schedule.validFrom ?? "",
                validTo: schedule.validTo ?? "",
                ...scheduleParams(schedule, t, locale),
              };
            });
          return { ...base, schedules };
        }),
    [rows, serviceReceiver, containerType, t, locale, today],
  );

  return { groups, pickupLocations, isLoading, error, schedulesFailed };
}

/** «Графики вывоза» rows, one per schedule; a schedule's own period stands in for the report's. */
export function useScheduleReportRows() {
  const { containerType, jobType, jobCategory } = useReportFilterValues();
  const { groups, isLoading, error, schedulesFailed } = useScheduledGroups("", containerType);

  const rows = useMemo(
    () =>
      groups
        .flatMap((group) => group.schedules)
        .filter(
          (row) =>
            (!jobType || row.jobType === jobType) &&
            (!jobCategory || row.jobCategory === jobCategory),
        )
        .sort(byPickupLocationThenJobType),
    [groups, jobType, jobCategory],
  );

  // One request: failed, the report has no schedules to show.
  return { rows, isLoading, error: error ?? (schedulesFailed || undefined) };
}

/** «Реестр площадок» rows: each pickup location with the container groups it hosts. */
export function usePickupLocationRegistryRows() {
  const { serviceReceiver, containerType } = useReportFilterValues();
  const { groups, pickupLocations, isLoading, error, schedulesFailed } = useScheduledGroups(
    serviceReceiver,
    containerType,
  );

  const rows = useMemo<PickupLocationRegistryRow[]>(() => {
    const byPickupLocation = new Map<string, typeof groups>();
    for (const group of groups) {
      const key = String(group.pickupLocationId);
      byPickupLocation.set(key, [...(byPickupLocation.get(key) ?? []), group]);
    }
    const withGroups = [...byPickupLocation.values()].map(
      (locationGroups): PickupLocationRegistryRow => {
        const schedules = locationGroups.flatMap((group) => group.schedules);
        const containers = new Map<string, number>();
        for (const group of locationGroups) {
          const name = group.containerTypeName || "—";
          containers.set(name, (containers.get(name) ?? 0) + group.numberOfContainers);
        }
        return {
          pickupLocationId: locationGroups[0].pickupLocationId,
          pickupLocationName: locationGroups[0].pickupLocationName,
          ownerOrganizations: [
            ...new Map(
              locationGroups
                .filter((group) => group.ownerOrganizationName)
                .map((group) => [
                  group.ownerOrganizationId,
                  { id: group.ownerOrganizationId, name: group.ownerOrganizationName },
                ]),
            ).values(),
          ].sort((a, b) => a.name.localeCompare(b.name)),
          jobCategories: distinct(schedules.map((row) => row.jobCategory)),
          containers: [...containers].map(([name, count]) => `${count} × ${name}`).join(", "),
          schedules: distinct(
            [...schedules]
              .sort((a, b) => a.jobTypeLabel.localeCompare(b.jobTypeLabel))
              .map((row) => `${row.jobTypeLabel}: ${row.params}`),
          ).join("; "),
        };
      },
    );
    // A pickup location without container groups is still a registered site. It can't
    // match an owner or container type filter, so it shows only while neither is set.
    const withoutGroups =
      serviceReceiver || containerType
        ? []
        : pickupLocations
            .filter((location) => !byPickupLocation.has(String(location.id)))
            .map(
              (location): PickupLocationRegistryRow => ({
                pickupLocationId: location.id,
                pickupLocationName: location.name,
                ownerOrganizations: [],
                jobCategories: [],
                containers: "",
                schedules: "",
              }),
            );
    return [...withGroups, ...withoutGroups].sort(
      (a, b) =>
        a.pickupLocationName.localeCompare(b.pickupLocationName) ||
        compareIds(a.pickupLocationId, b.pickupLocationId),
    );
  }, [groups, pickupLocations, serviceReceiver, containerType]);

  return { rows, isLoading, error, schedulesFailed };
}
